<template>
  <ul
    v-if="simple"
    class="ui-pagination tw-inline-flex tw-items-center tw-gap-2 tw-list-none tw-p-0 tw-m-0"
  >
    <li>
      <button
        type="button"
        :class="btnClass"
        :disabled="modelValue <= 1"
        @click="setPage(modelValue - 1)"
      >
        Previous
      </button>
    </li>
    <li>
      <button
        type="button"
        :class="btnClass"
        :disabled="modelValue >= totalPages"
        @click="setPage(modelValue + 1)"
      >
        Next
      </button>
    </li>
  </ul>
  <ul
    v-else
    class="ui-pagination tw-inline-flex tw-items-center tw-gap-0.5 tw-list-none tw-p-0 tw-m-0"
  >
    <li>
      <button
        type="button"
        :class="btnClass"
        :disabled="modelValue <= 1"
        @click="setPage(modelValue - 1)"
        aria-label="Previous page"
      >
        &lsaquo;
      </button>
    </li>
    <li v-for="(p, i) in pages" :key="i">
      <button
        v-if="typeof p === 'number'"
        type="button"
        :class="[btnClass, p === modelValue && activeClass]"
        :aria-current="p === modelValue ? 'page' : undefined"
        @click="setPage(p)"
      >
        {{ p }}
      </button>
      <span
        v-else
        :class="btnClass"
        class="tw-cursor-default tw-pointer-events-none"
        aria-hidden="true"
      >
        &hellip;
      </span>
    </li>
    <li>
      <button
        type="button"
        :class="btnClass"
        :disabled="modelValue >= totalPages"
        @click="setPage(modelValue + 1)"
        aria-label="Next page"
      >
        &rsaquo;
      </button>
    </li>
  </ul>
</template>

<script setup lang="ts">
  /**
   * UiPagination — Tailwind primitive replacing BootstrapVue-Next's
   * `b-pagination`. Mirrors the props PagedTable was already passing:
   * `total-rows`, `per-page`, `size`, and v-model on the current page
   * (1-indexed).
   *
   * Page-list algorithm: shows all pages when total ≤ 7; otherwise
   * shows page 1, an ellipsis when needed, a window of ±2 around the
   * current page, another ellipsis when needed, and the last page.
   * Mirrors the visual density Bootstrap uses without trying to
   * reproduce the exact thresholds.
   */
  import { computed } from 'vue'

  defineOptions({ name: 'UiPagination' })

  interface Props {
    modelValue: number
    totalRows: number
    perPage: number
    size?: 'sm' | 'md' | 'lg'
    /** Render only Previous / Next buttons (no page numbers). */
    simple?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    simple: false,
  })

  const emit = defineEmits<{
    'update:modelValue': [value: number]
  }>()

  const totalPages = computed(() =>
    Math.max(1, Math.ceil(props.totalRows / Math.max(1, props.perPage)))
  )

  const pages = computed<(number | '…')[]>(() => {
    const total = totalPages.value
    const cur = props.modelValue
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }
    const range: (number | '…')[] = [1]
    if (cur > 4) range.push('…')
    const start = Math.max(2, cur - 2)
    const end = Math.min(total - 1, cur + 2)
    for (let i = start; i <= end; i++) range.push(i)
    if (cur < total - 3) range.push('…')
    range.push(total)
    return range
  })

  const SIZES: Record<NonNullable<Props['size']>, string> = {
    sm: 'tw-px-2 tw-py-0.5 tw-text-xs tw-min-w-[1.75rem]',
    md: 'tw-px-3 tw-py-1 tw-text-sm tw-min-w-[2.25rem]',
    lg: 'tw-px-4 tw-py-2 tw-text-base tw-min-w-[2.75rem]',
  }
  // Simple mode renders "Previous" / "Next" text labels — give them roomier
  // padding so they don't look cramped.
  const SIMPLE_SIZES: Record<NonNullable<Props['size']>, string> = {
    sm: 'tw-px-3 tw-py-1 tw-text-sm',
    md: 'tw-px-4 tw-py-1.5 tw-text-sm',
    lg: 'tw-px-5 tw-py-2 tw-text-base',
  }

  const btnClass = computed(() => [
    'tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 tw-leading-none hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white',
    props.simple ? SIMPLE_SIZES[props.size] : SIZES[props.size],
  ])

  const activeClass = 'tw-bg-blue-600 tw-text-white tw-border-blue-600 hover:tw-bg-blue-600'

  function setPage (n: number) {
    if (n < 1 || n > totalPages.value || n === props.modelValue) return
    emit('update:modelValue', n)
  }
</script>
